import type {
  CameraOptions,
  CollisionWorld,
  EulerRotation,
  ModelLoadOptions,
  ProjectedPoint,
  RaycastHit,
  RaycastOptions,
  RenderableObject,
  RendererAdapter,
  SceneOptions,
  ScreenshotOptions,
  SplatLoadOptions,
  Transform,
  ViewerNavigationMode,
  WalkPhysicsConfig,
} from '@mindcloudxai/shared';
import { resolveAssetUrl, uid, Vec3 } from '@mindcloudxai/shared';
import { NavigationManager } from '@mindcloudxai_3d/core';
import type { NavigationCallbacks, NavigationModeKey } from '@mindcloudxai_3d/core';
import { SparkRenderer, SplatMesh } from '@sparkjsdev/spark';
import * as THREE from 'three';
import { CollisionMeshLoader } from './CollisionMeshLoader';
import { loadCollisionMesh } from './collision';

export { CollisionMeshLoader } from './CollisionMeshLoader';
export type { CollisionMeshLoadResult } from './CollisionMeshLoader';
export { MeshCollisionWorld, applyCollisionDebugMaterial, loadCollisionMesh } from './collision';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function applyTransform(object: THREE.Object3D, transform?: Transform): void {
  if (!transform) return;
  if (transform.position) {
    object.position.set(transform.position.x, transform.position.y, transform.position.z);
  }
  if (transform.rotation) {
    object.rotation.set(
      transform.rotation.x,
      transform.rotation.y,
      transform.rotation.z,
      transform.rotation.order,
    );
  }
  if (typeof transform.scale === 'number') {
    object.scale.setScalar(transform.scale);
  } else if (transform.scale) {
    object.scale.set(transform.scale.x, transform.scale.y, transform.scale.z);
  }
}

/**
 * Compose two Euler rotations via quaternion multiplication so that
 * `baseRotation` is always preserved in the final orientation, even when a
 * caller-supplied `transform.rotation` is also present. Required by the
 * `SceneOptions.baseRotation` contract: base must be applied BEFORE user
 * rotation, and the two must be composable rather than one overwriting the other.
 */
function composeRotation(base?: EulerRotation, user?: EulerRotation): EulerRotation | undefined {
  if (!base && !user) return undefined;
  const order = base?.order ?? user?.order ?? 'XYZ';
  const eBase = new THREE.Euler(base?.x ?? 0, base?.y ?? 0, base?.z ?? 0, base?.order ?? order);
  const eUser = new THREE.Euler(user?.x ?? 0, user?.y ?? 0, user?.z ?? 0, user?.order ?? order);
  const q = new THREE.Quaternion()
    .setFromEuler(eBase)
    .multiply(new THREE.Quaternion().setFromEuler(eUser));
  const out = new THREE.Euler().setFromQuaternion(q, order);
  return { x: out.x, y: out.y, z: out.z, order };
}

// ---------------------------------------------------------------------------
// Three.js adapter (powered by @sparkjsdev/spark for gaussian splatting)
// ---------------------------------------------------------------------------

export class ThreeAdapter implements RendererAdapter {
  readonly id = 'three' as const;
  readonly label = 'Three.js (Spark)';

  private camera?: THREE.PerspectiveCamera;
  private navManager: NavigationManager | null = null;
  private navModeListeners = new Set<(mode: ViewerNavigationMode) => void>();
  private currentNavMode: NavigationModeKey = 'orbit';
  private storedCollisionWorld: CollisionWorld | null = null;
  private storedWalkPhysics: WalkPhysicsConfig | null = null;
  private webglRenderer?: THREE.WebGLRenderer;
  private scene?: THREE.Scene;
  private spark?: SparkRenderer;
  private objects = new Map<string, THREE.Object3D>();
  private revokeUrls: string[] = [];
  private cameraTarget = new THREE.Vector3();
  private flyAbort: (() => void) | null = null;
  /**
   * Scene-wide base rotation applied to every splat. Provided at init time so
   * source-data up-axis corrections (e.g. Z-up → Y-up) stay in business code;
   * the SDK only knows it has a rotation to apply.
   */
  private baseRotation?: EulerRotation;

  async init(container: HTMLElement, options: SceneOptions): Promise<void> {
    this.dispose();
    this.baseRotation = options.baseRotation;
    container.replaceChildren();

    const width = Math.max(container.clientWidth, 1);
    const height = Math.max(container.clientHeight, 1);

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(options.background ?? '#020617');

    const cam = options.camera;
    this.camera = new THREE.PerspectiveCamera(
      cam.fov ?? 60,
      width / height,
      cam.near ?? 0.01,
      cam.far ?? 1000,
    );

    this.webglRenderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    this.webglRenderer.setPixelRatio(window.devicePixelRatio);
    this.webglRenderer.setSize(width, height);
    container.appendChild(this.webglRenderer.domElement);

    this.spark = new SparkRenderer({ renderer: this.webglRenderer });
    this.scene.add(this.spark);

    this.setCamera(cam);

    // NavigationManager 接管相机控制，替代 OrbitControls
    this.navManager = new NavigationManager();
    this.navManager.init({
      renderer: this,
      canvas: this.webglRenderer.domElement,
      callbacks: this.buildNavCallbacks(),
    });
    if (this.storedCollisionWorld) this.navManager.setCollisionWorld(this.storedCollisionWorld);
    if (this.storedWalkPhysics) this.navManager.setWalkPhysics(this.storedWalkPhysics);
    this.navManager.setMode(this.currentNavMode);

    this.webglRenderer.setAnimationLoop(() => {
      this.render();
    });
  }

  async loadSplat(options: SplatLoadOptions): Promise<RenderableObject> {
    const { url, revokeOnDispose } = resolveAssetUrl(options);
    if (revokeOnDispose) this.revokeUrls.push(revokeOnDispose);

    const source = options.file
      ? { fileBytes: await options.file.arrayBuffer(), fileName: options.file.name }
      : { url, fileName: url.split('/').pop() ?? 'splat' };

    const splat = new SplatMesh({ ...source, onLoad: () => this.render() });
    await splat.initialized;

    // -----------------------------------------------------------------------
    // Compose scene-wide base rotation with caller-supplied transform.rotation
    // via quaternion multiplication. baseRotation is applied BEFORE user
    // rotation so it always participates in the final orientation, regardless
    // of whether the caller supplies their own per-splat rotation. Caller
    // decides what baseRotation means (e.g. Z-up → Y-up axis swap);
    // the SDK is unaware of those conventions.
    // -----------------------------------------------------------------------
    const composed = composeRotation(this.baseRotation, options.transform?.rotation);
    if (composed) {
      splat.rotation.set(composed.x, composed.y, composed.z, composed.order);
    }

    // Apply only position/scale here; rotation has already been composed above
    // and writing it again via applyTransform would overwrite the composition.
    applyTransform(
      splat,
      options.transform ? { ...options.transform, rotation: undefined } : undefined,
    );

    const id = uid('splat');
    const result: RenderableObject = {
      id,
      type: 'splat',
      visible: true,
      transform: options.transform,
      native: splat,
    };

    // collisionUrl 传入时内部加载 GLB 并构建碰撞世界，避免业务层单独处理碰撞流程。
    if (options.collisionUrl) {
      try {
        const collision = await loadCollisionMesh({
          url: options.collisionUrl,
          debug: options.debugCollision,
        });
        this.setCollisionWorld(collision.world);
        if (options.debugCollision && this.scene) {
          const debugMesh = collision.native as THREE.Object3D;
          const debugId = uid('collision-debug');
          this.objects.set(debugId, debugMesh);
          this.scene.add(debugMesh);
        }
      } catch (err) {
        console.warn('[ThreeAdapter] Failed to load collision mesh:', err);
      }
    }

    return result;
  }

  async loadModel(options: ModelLoadOptions): Promise<RenderableObject> {
    const url = options.url;
    if (!url) throw new Error('loadModel requires a URL');
    const loader = new CollisionMeshLoader();
    const { group } = await loader.load(url);
    applyTransform(group, options.transform);
    const id = uid('model');
    return { id, type: 'mesh', visible: true, transform: options.transform, native: group };
  }

  addToScene(object: RenderableObject): void {
    if (!this.scene || !(object.native instanceof THREE.Object3D)) return;
    this.objects.set(object.id, object.native);
    this.scene.add(object.native);
  }

  remove(objectId: string): void {
    const obj = this.objects.get(objectId);
    if (obj && this.scene) this.scene.remove(obj);
    this.objects.delete(objectId);
  }

  setCamera(camera: CameraOptions): void {
    if (!this.camera) return;
    this.camera.position.set(camera.position.x, camera.position.y, camera.position.z);
    this.cameraTarget.set(camera.target.x, camera.target.y, camera.target.z);
    if (camera.fov !== undefined) this.camera.fov = camera.fov;
    if (camera.near !== undefined) this.camera.near = camera.near;
    if (camera.far !== undefined) this.camera.far = camera.far;
    this.camera.lookAt(camera.target.x, camera.target.y, camera.target.z);
    this.camera.updateProjectionMatrix();
  }

  getCamera(): CameraOptions {
    if (!this.camera) {
      throw new Error('ThreeAdapter is not initialized');
    }
    return {
      position: {
        x: this.camera.position.x,
        y: this.camera.position.y,
        z: this.camera.position.z,
      },
      target: {
        x: this.cameraTarget.x,
        y: this.cameraTarget.y,
        z: this.cameraTarget.z,
      },
      fov: this.camera.fov,
      near: this.camera.near,
      far: this.camera.far,
    };
  }

  async flyToCamera(camera: CameraOptions, options?: { durationMs?: number }): Promise<void> {
    if (!this.camera) return;
    const from = this.getCamera();
    const durationMs = Math.max(options?.durationMs ?? 600, 0);
    this.flyAbort?.();
    if (durationMs === 0) {
      this.setCamera(camera);
      return;
    }

    await new Promise<void>((resolve) => {
      let done = false;
      const start = performance.now();
      this.flyAbort = () => {
        if (done) return;
        done = true;
        this.flyAbort = null;
        resolve();
      };
      const step = (now: number) => {
        if (done) return;
        const t = Math.min((now - start) / durationMs, 1);
        const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        this.setCamera({
          position: {
            x: from.position.x + (camera.position.x - from.position.x) * eased,
            y: from.position.y + (camera.position.y - from.position.y) * eased,
            z: from.position.z + (camera.position.z - from.position.z) * eased,
          },
          target: {
            x: from.target.x + (camera.target.x - from.target.x) * eased,
            y: from.target.y + (camera.target.y - from.target.y) * eased,
            z: from.target.z + (camera.target.z - from.target.z) * eased,
          },
          fov: camera.fov ?? from.fov,
          near: camera.near ?? from.near,
          far: camera.far ?? from.far,
        });
        if (t >= 1) {
          done = true;
          this.flyAbort = null;
          resolve();
          return;
        }
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }

  async captureScreenshot(options?: ScreenshotOptions): Promise<Blob> {
    if (!this.webglRenderer) {
      throw new Error('ThreeAdapter is not initialized');
    }
    this.render();
    const canvas = this.webglRenderer.domElement;
    const mimeType = options?.mimeType ?? 'image/png';
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
            return;
          }
          reject(new Error('Failed to capture screenshot'));
        },
        mimeType,
        options?.quality,
      );
    });
  }

  raycast(options: RaycastOptions): RaycastHit | null {
    if (!this.camera || !this.webglRenderer) return null;
    const rect = this.webglRenderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((options.clientX - rect.left) / rect.width) * 2 - 1,
      -(((options.clientY - rect.top) / rect.height) * 2 - 1),
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, this.camera);
    const objects = [...this.objects.entries()];
    const objectIds = new Map(objects.map(([id, object]) => [object, id]));
    const hits = raycaster.intersectObjects(
      objects.map(([, object]) => object),
      true,
    );
    const hit = hits[0];
    if (!hit) return null;
    let current: THREE.Object3D | null = hit.object;
    let objectId: string | undefined;
    while (current && !objectId) {
      objectId = objectIds.get(current);
      current = current.parent;
    }
    return {
      objectId,
      point: new Vec3(hit.point.x, hit.point.y, hit.point.z),
      distance: hit.distance,
    };
  }

  projectPoint(point: Vec3): ProjectedPoint | null {
    if (!this.camera || !this.webglRenderer) return null;
    const rect = this.webglRenderer.domElement.getBoundingClientRect();
    const projected = new THREE.Vector3(point.x, point.y, point.z).project(this.camera);
    return {
      clientX: rect.left + ((projected.x + 1) / 2) * rect.width,
      clientY: rect.top + ((1 - projected.y) / 2) * rect.height,
      depth: projected.z,
      visible:
        projected.x >= -1 &&
        projected.x <= 1 &&
        projected.y >= -1 &&
        projected.y <= 1 &&
        projected.z >= -1 &&
        projected.z <= 1,
    };
  }

  resize(width: number, height: number): void {
    if (!this.camera || !this.webglRenderer) return;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.webglRenderer.setSize(width, height);
  }

  render(): void {
    if (this.webglRenderer && this.scene && this.camera) {
      this.webglRenderer.render(this.scene, this.camera);
    }
  }

  dispose(): void {
    this.flyAbort?.();
    this.webglRenderer?.setAnimationLoop(null);
    this.navManager?.dispose();
    this.navManager = null;
    this.navModeListeners.clear();
    this.objects.forEach((obj) => obj.parent?.remove(obj));
    this.objects.clear();
    if (this.spark && this.scene) {
      this.scene.remove(this.spark as unknown as THREE.Object3D);
    }
    this.webglRenderer?.dispose();
    this.webglRenderer?.domElement.remove();
    this.revokeUrls.forEach((u) => URL.revokeObjectURL(u));
    this.revokeUrls = [];
    this.camera = undefined;
    this.webglRenderer = undefined;
    this.scene = undefined;
    this.spark = undefined;
  }

  setNavigationMode(mode: ViewerNavigationMode): void {
    if (mode === 'anim') return;
    const resolvedMode = mode === 'avatar' ? 'walk' : mode;
    this.currentNavMode = resolvedMode;
    this.navManager?.setMode(resolvedMode);
  }

  resetNavigation(camera: CameraOptions): void {
    this.setCamera(camera);
    this.navManager?.resetCurrentMode();
  }

  getNavigationMode(): ViewerNavigationMode {
    return this.currentNavMode;
  }

  isWalkAvailable(): boolean {
    return this.storedCollisionWorld !== null;
  }

  onNavigationModeChange(cb: (mode: ViewerNavigationMode) => void): void {
    this.navModeListeners.add(cb);
  }

  offNavigationModeChange(cb: (mode: ViewerNavigationMode) => void): void {
    this.navModeListeners.delete(cb);
  }

  setCollisionWorld(world: CollisionWorld): void {
    this.storedCollisionWorld = world;
    this.navManager?.setCollisionWorld(world);
  }

  setWalkPhysics(config: WalkPhysicsConfig): void {
    this.storedWalkPhysics = config;
    this.navManager?.setWalkPhysics(config);
  }

  private buildNavCallbacks(): NavigationCallbacks {
    return {
      onModeChange: (mode) => {
        this.currentNavMode = mode;
        this.navModeListeners.forEach((cb) => cb(mode));
      },
    };
  }
}

/** Factory function for the Three.js adapter. */
export function createThreeAdapter(): RendererAdapter {
  return new ThreeAdapter();
}
